import { useState } from 'react';
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { useNavigate, Link } from 'react-router-dom';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import userService from '../../../services/userService';
import authService from '../../../services/authService';
import React from 'react';
import securityUtils from '../../../services/securityUtils';
import { usePortalName } from '../../../hooks/usePortalName';
import PasswordRequirements from './PasswordRequirements';
import SocialButtons from './SocialButtons';
import * as userSlice from '../../../data/user.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

const PREFIX = 'CreateAccountPageForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  formBody: `${PREFIX}-formBody`,
  title: `${PREFIX}-title`,
  linkText: `${PREFIX}-linkText`,
  submit: `${PREFIX}-submit`,
  dividerWrapper: `${PREFIX}-dividerWrapper`,
  dividerLine: `${PREFIX}-dividerLine`,
  dividerText: `${PREFIX}-dividerText`,
  passwordWrapper: `${PREFIX}-passwordWrapper`,
  inlineError: `${PREFIX}-inlineError`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: theme.palette.secondary.main,
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  [`& .${classes.form}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  [`& .${classes.formBody}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    width: '100%',
  },
  [`& .${classes.linkText}`]: {
    textTransform: 'none',
    justifyContent: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
    fontWeight: 400,
    color: theme.palette.buttonColor.main,
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.buttonHoverColor.main,
      textDecoration: 'underline',
      backgroundColor: theme.palette.background.default,
    },
  },
  [`& .${classes.submit}`]: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  [`& .${classes.dividerWrapper}`]: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),
  },
  [`& .${classes.dividerLine}`]: {
    flex: 1,
    height: '1px',
    backgroundColor: theme.palette.divider,
  },
  [`& .${classes.dividerText}`]: {
    fontWeight: 600,
  },
  [`& .${classes.inlineError}`]: {
    marginTop: theme.spacing(-2),
    paddingLeft: theme.spacing(0.5),
    fontSize: 14,
  },
}));

export default function CreateAccountForm() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register, handleSubmit, formState, watch } = useForm();
  const password = watch('password');
  const portalName = usePortalName();

  const passwordChecks = {
    lowercase: /[a-z]/,
    uppercase: /[A-Z]/,
    number: /[0-9]/,
    special: /[!@#$%.]/,
    minLength: 8,
  };

  const [createAccountError, setCreateAccountError] =
    React.useState<string>('');

  const getCallbackUrl = (): string => {
    const url = window.location.href;
    const idx = url.lastIndexOf('/');
    return `${url.substring(0, idx)}/memberVerifyEmail`;
  };

  const onSubmit = (data: any) => {
    const cbUrl = securityUtils.getPortalCallbackURL(portalName || '');
    const logoUrl = securityUtils.getPortalLogoURL(portalName || '');
    const clientBC = securityUtils.getPortalClientBC(portalName || '');
    userService
      .onboardMemberUser(
        data.email,
        data.confirmPassword,
        cbUrl,
        logoUrl,
        clientBC,
      )
      .subscribe({
        next: (onboardResponse: any) => {
          if (onboardResponse) {
            authService
              .memberLogin(data.email, data.confirmPassword)
              .then(() => {
                dispatch(userSlice.setLoggedInUserInitialized(false));
                dispatch(userSlice.fetchLoggedInUserInfoAsync());
                const portalName = usePortalName();
                const callbackUrl = getCallbackUrl();
                const logoUrl = securityUtils.getPortalLogoURL(
                  portalName || '',
                );
                userService.sendVerificationEmail(
                  clientBC,
                  callbackUrl,
                  logoUrl,
                );
                navigate('/memberVerifyAccount');
              });
          } else {
            setCreateAccountError(t('member.failedToCreateAccountError'));
          }
        },
        error: (err) => {
          if (err.message) {
            setCreateAccountError(String(err.message) || '');
          } else {
            setCreateAccountError(String(err) || '');
          }
        },
      });
  };

  return (
    <Root className={classes.container}>
      <Box className={classes.form}>
        <Typography variant="h3" component="h1" className={classes.title}>
          {t('member.createAccount')}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} className={classes.formBody}>
          <TextField
            fullWidth
            label={t('client.email')}
            variant="outlined"
            required
            autoComplete="email"
            {...register('email', {
              required: true,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('member.invalidEmailAddress'),
              },
            })}
          />
          {formState.errors['email'] && (
            <FormHelperText error className={classes.inlineError}>
              {String(formState.errors['email'].message || '')}
            </FormHelperText>
          )}
          <Box className={classes.passwordWrapper}>
            <TextField
              fullWidth
              label={t('users.password')}
              variant="outlined"
              required
              type={showPassword ? 'text' : 'password'}
              value={password || ''}
              autoComplete="new-password"
              {...register('password', {
                required: true,
                validate: (value) =>
                  (value.length >= passwordChecks.minLength &&
                    passwordChecks.lowercase.test(value) &&
                    passwordChecks.number.test(value) &&
                    passwordChecks.special.test(value) &&
                    passwordChecks.uppercase.test(value)) ||
                  t('member.invalidPassword'),
              })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={t('login.togglePassword')}>
                      {showPassword ? (
                        <VisibilityOffOutlined />
                      ) : (
                        <VisibilityOutlined />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {!!password && <PasswordRequirements password={password} />}
          </Box>

          <TextField
            fullWidth
            label={t('member.confirmPassword')}
            variant="outlined"
            required
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            {...register('confirmPassword', {
              required: true,
              validate: (value) =>
                password === value || t('member.passwordMismatch'),
            })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label={t('login.toggleConfirmPassword')}
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }>
                    {showConfirmPassword ? (
                      <VisibilityOffOutlined />
                    ) : (
                      <VisibilityOutlined />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {formState.errors['confirmPassword'] && (
            <FormHelperText error className={classes.inlineError}>
              {String(formState.errors['confirmPassword'].message || '')}
            </FormHelperText>
          )}

          {createAccountError && (
            <Typography variant="body2" color="error">
              {createAccountError}
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            size="large"
            className={classes.submit}
            type="submit">
            {t('member.continue')}
          </Button>
        </form>

        <Typography variant="body2">
          {t('member.alreadyHaveAccount')}{' '}
          <Link to={'/login'} className={classes.linkText}>
            {t('member.login')}
          </Link>
        </Typography>

        <Box className={classes.dividerWrapper}>
          <Box className={classes.dividerLine} />
          <Typography
            variant="h5"
            component="p"
            className={classes.dividerText}>
            {t('member.or')}
          </Typography>
          <Box className={classes.dividerLine} />
        </Box>

        <SocialButtons />
      </Box>
    </Root>
  );
}
